import React, { Children, cloneElement, useMemo } from 'react'

import {
  AccountSwitcher,
  Box,
  Masthead,
  Link,
  BDSProvider,
  AccountSwitcherAccount
} from '@barracuda-internal/bds-core-v2'

import ConnectO365Dialog from 'global/components/lib/dialogs/connectO365/ConnectO365Dialog'
import uselayoutLogic from 'global/components/lib/layout/useLayoutLogic'
import { navbarHeight } from 'global/components/lib/layout/navbar/navbarStyles'
import LicenseBanner from 'global/components/lib/layout/licenseBanner/LicenseBanner'
import MaintenanceBanner from 'global/components/lib/layout/maintenanceBanner/MaintenanceBanner'
import NavbarImpersonationBanner from 'global/components/lib/layout/navbar/impersonationBanner/NavbarImpersonationBanner'
import PermissionBanner from 'global/components/lib/layout/permissionBanner/PermissionBanner'
import FeatureBanner from 'global/components/lib/layout/featureBanner/FeatureBanner'
import styles from 'global/components/lib/layout/layoutStyles'
import { config } from 'global/lib/config'

import 'global/types/mui/index'

import { NavbarContextMenuO365Props } from './navbar'

export type GlobalLayoutProps = {
  children: any
  isFromSharedLink?: boolean
  initAccount?: string
  isFlexLayout?: boolean
  isLicenseBannerEnabled?: boolean
  isMaintenanceBannerEnabled?: boolean
  isMastheadEnabled?: boolean
  isNewLayout?: boolean
  isPermissionBannerEnabled?: boolean
  isUnifiedReportingBannerEnabled?: boolean
  navbar?: JSX.Element
  navbarContextMenuO365Props?: NavbarContextMenuO365Props
  onSelectAccountCallback?: (selectedAccount: AccountSwitcherAccount) => void
  pageSubtitle?: string
  pageTitle?: string
  rightContent?: JSX.Element
  showConvergedMessageLogLink?: boolean
}

// TODO: Import theme directly when BDS dependency is updated with theme fix
function Layout({
  children,
  isFromSharedLink = false,
  initAccount,
  isFlexLayout = false,
  isLicenseBannerEnabled = true,
  isMaintenanceBannerEnabled = false,
  isMastheadEnabled = true,
  isNewLayout = false,
  isPermissionBannerEnabled = false,
  isUnifiedReportingBannerEnabled = false,
  navbar,
  navbarContextMenuO365Props = {},
  onSelectAccountCallback,
  pageSubtitle,
  pageTitle = '',
  rightContent,
  showConvergedMessageLogLink = false,
  ...rest
}: GlobalLayoutProps): JSX.Element {
  const classes = styles()
  const [layoutConfig] = uselayoutLogic()
  const {
    accessTokenId,
    accounts,
    addMSTenantText,
    closeText,
    manageAccountText,
    convergedMessageLogLinkLabel,
    isConvergedMessageLogFeatureOn,
    isDialogOpened,
    isLoggedOut,
    isNavBarVisible,
    navbarImpersonationBannerProps,
    productTitle,
    sendNavO365TrackingEvent,
    toggleDialog
  } = layoutConfig

  const showNavBar = !!(isNavBarVisible && !isLoggedOut)

  // TODO: Remove this and make onSelectAccountCallback required when all apps use the new layout
  const noop = () => null // Define a noop function that does nothing

  return useMemo(() => {
    if (isNewLayout) {
      return (
        <BDSProvider>
          <Box className={classes.app}>
            <Box>{showNavBar && navbar}</Box>
            <Box className={`${classes.main} ${!showNavBar && (isFromSharedLink ? '' : classes.mainFullWidth)}`}>
              <Box className={isFlexLayout ? classes.flexContent : classes.content}>
                {!isLoggedOut && <NavbarImpersonationBanner {...navbarImpersonationBannerProps} />}
                {!isLoggedOut && isLicenseBannerEnabled && <LicenseBanner />}
                {!isLoggedOut && isMaintenanceBannerEnabled && <MaintenanceBanner />}
                {!isLoggedOut && isPermissionBannerEnabled && <PermissionBanner />}
                {!isLoggedOut && isUnifiedReportingBannerEnabled && <FeatureBanner />}
                {isMastheadEnabled && (
                  <Masthead
                    bannerContent={
                      isFromSharedLink ? (
                        undefined
                      ) : (
                        <>
                          <AccountSwitcher
                            accounts={accounts}
                            initAccount={initAccount}
                            onSelectAccountCallback={onSelectAccountCallback || noop}
                            closeText={closeText}
                            addMSTenantText={addMSTenantText}
                            manageAccountText={manageAccountText}
                            onAddMSTenantClick={() => {
                              toggleDialog()
                              sendNavO365TrackingEvent()
                            }}
                            onManageAccountClick={(): void => {
                              window.open(config.mspUrl, '_blank')
                            }}
                          />
                          {/* Temporary addition to allow for navigation to CML but I hate this and if it's going to be a thing, we need to modify BDS - Chris M. */}
                          {showConvergedMessageLogLink && isConvergedMessageLogFeatureOn && (
                            <Box className={classes.convergedMessageLogLink}>
                              <Link underline="always" href={config.convergedMessageLogUrl}>
                                {convergedMessageLogLinkLabel}
                              </Link>
                            </Box>
                          )}
                        </>
                      )
                    }
                    rightContent={rightContent}
                    productTitle={productTitle}
                    pageTitle={pageTitle}
                    pageSubtitle={pageSubtitle}
                  />
                )}
                {!isMastheadEnabled &&
                  (isFromSharedLink ? (
                    undefined
                  ) : (
                    /* Wrapper added to allow for relative position to be set */
                    <div className={classes.accountSwitcherWrapper}>
                      <AccountSwitcher
                        accounts={accounts}
                        initAccount={initAccount}
                        onSelectAccountCallback={onSelectAccountCallback || noop}
                        closeText={closeText}
                        addMSTenantText={addMSTenantText}
                        manageAccountText={manageAccountText}
                        onAddMSTenantClick={() => {
                          toggleDialog()
                          sendNavO365TrackingEvent()
                        }}
                        onManageAccountClick={(): void => {
                          window.open(config.bccAccountUserUrl, '_blank')
                        }}
                      />
                      {/* Temporary addition to allow for navigation to CML but I hate this and if it's going to be a thing, we need to modify BDS - Chris M. */}
                      {showConvergedMessageLogLink && isConvergedMessageLogFeatureOn && (
                        <Box className={classes.convergedMessageLogLink}>
                          <Link underline="always" href={config.convergedMessageLogUrl}>
                            {convergedMessageLogLinkLabel}
                          </Link>
                        </Box>
                      )}
                    </div>
                  ))}
                <ConnectO365Dialog
                  toggleDialog={toggleDialog}
                  isDialogOpened={isDialogOpened}
                  accessTokenId={accessTokenId}
                  successCb={navbarContextMenuO365Props.successCb}
                  showScanTypeSelection={navbarContextMenuO365Props.showScanTypeSelection}
                  accounts={navbarContextMenuO365Props.accounts}
                />
                {children}
              </Box>
            </Box>
          </Box>
        </BDSProvider>
      )
    }

    return (
      <div className="App">
        {!isLoggedOut && navbar}
        <div className="content" style={{ paddingTop: navbarHeight }}>
          {!isLoggedOut && <NavbarImpersonationBanner {...navbarImpersonationBannerProps} />}
          {!isLoggedOut && isLicenseBannerEnabled && <LicenseBanner />}
          {!isLoggedOut && isMaintenanceBannerEnabled && <MaintenanceBanner />}
          {!isLoggedOut && isPermissionBannerEnabled && <PermissionBanner />}
          {!isLoggedOut && isUnifiedReportingBannerEnabled && <FeatureBanner />}
          {Children.map(children, child => cloneElement(child as React.ReactElement<any>, { ...rest }))}
        </div>
      </div>
    )
  }, [
    isNewLayout,
    isLoggedOut,
    navbar,
    navbarImpersonationBannerProps,
    isLicenseBannerEnabled,
    isMaintenanceBannerEnabled,
    isPermissionBannerEnabled,
    isUnifiedReportingBannerEnabled,
    children,
    classes.app,
    classes.main,
    classes.mainFullWidth,
    classes.flexContent,
    classes.content,
    classes.convergedMessageLogLink,
    classes.accountSwitcherWrapper,
    showNavBar,
    isFromSharedLink,
    isFlexLayout,
    isMastheadEnabled,
    accounts,
    initAccount,
    onSelectAccountCallback,
    closeText,
    addMSTenantText,
    manageAccountText,
    showConvergedMessageLogLink,
    isConvergedMessageLogFeatureOn,
    convergedMessageLogLinkLabel,
    rightContent,
    productTitle,
    pageTitle,
    pageSubtitle,
    toggleDialog,
    isDialogOpened,
    accessTokenId,
    navbarContextMenuO365Props.successCb,
    navbarContextMenuO365Props.showScanTypeSelection,
    navbarContextMenuO365Props.accounts,
    sendNavO365TrackingEvent,
    rest
  ])
}

export default Layout
